import React, { Suspense } from "react";
import { BrowserRouter } from "react-router-dom";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloak, { keycloakInitOptions } from "./Keycloak";
import { TimezoneProvider } from "./context/TimezoneContext";
import { useTranslation } from "react-i18next";
import { useUiVersion } from "./components/common/useUiVersion";
import LazyTreeBoundary, { clearLazyTreeReloadLatch } from "./components/common/LazyTreeBoundary";
import LegacyOnlyRedirect from "./components/common/LegacyOnlyRedirect";
// GUNDI-5665: new is the default tree and is normally imported DIRECTLY —
// every visit renders it, and a lazy chunk would only add a fetch and a
// skeleton flash to the common path. That is inverted below for as long as the
// LEGACY_ONLY kill switch is on; see the restore checklist there.
import { LEGACY_ONLY } from "./killSwitch";
// TEMPORARY (LEGACY_ONLY kill switch): legacy is the only tree every visit
// renders, so it ships in the main bundle — a lazy chunk would only show the
// TreeLoading skeleton on every reload — and the new tree, which nobody can
// reach, moves behind React.lazy so it is not downloaded at all. To restore
// the new UI: flip the flag, put back `import NewApp from "./NewApp";`, and
// remove this import, the lazy NewApp below, and the `if (LEGACY_ONLY)` line
// in VersionedTree — the line references this import, so removing one
// without the other fails the build.
import LegacyAppEager from "./legacy/LegacyApp";
const NewApp = React.lazy(() =>
  import("./NewApp").then((module) => {
    // Same latch reset the legacy lazy import does: without it a second failed
    // chunk load in one session lands on the error panel with no retry.
    clearLazyTreeReloadLatch();
    return module;
  })
);

// GUNDI-5665: the frozen legacy portal, in its own chunk so browsers that never
// take the banner's opt-out never download it.
const LegacyApp = React.lazy(() =>
  import("./legacy/LegacyApp").then((module) => {
    // Chunk loaded: a past reload-recovery latch is spent, arm the next one.
    clearLazyTreeReloadLatch();
    return module;
  })
);

// Skeleton while the legacy chunk downloads: no copy, so it needs no keys.
function TreeLoading() {
  return (
    <div className="px-8 desktop:px-[60px] py-6 pt-[115px]" data-testid="TreeLoading-div">
      <div className="h-8 w-64 mb-6 bg-teal-t30 rounded animate-pulse" />
      {[...Array(8)].map((_, i) => (
        <div key={i} className="h-4 w-full max-w-3xl mb-3 bg-teal-t30 rounded animate-pulse" />
      ))}
    </div>
  );
}

function TreeLoadFailed() {
  const { t } = useTranslation();
  return (
    <div className="px-8 desktop:px-[60px] py-6 pt-[115px] text-base text-ui-text-primary-text" data-testid="TreeLoadFailed-div">
      {t("Something went wrong. Please try again or contact support.")}
    </div>
  );
}

// The version flag alone decides which tree owns the paths — both trees keep
// their own full route table, their own Nav, help panel, toasts and 404, and
// only one of them ever mounts. Mounting one tree inside the other's chrome
// instead would put the wrong navbar over the pages.
function VersionedTree() {
  const version = useUiVersion();
  // Same error boundary the lazy branches use: it catches ordinary render
  // errors too, so a throw inside legacy shows the failure panel instead of
  // unmounting the root and leaving a blank page.
  if (LEGACY_ONLY) {
    return (
      <LazyTreeBoundary fallback={<TreeLoadFailed />}>
        {/* No skeleton while the translation resolves: the lookup is a few
            hundred ms and a placeholder page that flashes and vanishes reads
            as a glitch. Nothing is shown until the real page is ready. */}
        <LegacyOnlyRedirect fallback={null}>
          <LegacyAppEager />
        </LegacyOnlyRedirect>
      </LazyTreeBoundary>
    );
  }
  if (version === "legacy") {
    return (
      <LazyTreeBoundary fallback={<TreeLoadFailed />}>
        <Suspense fallback={<TreeLoading />}>
          <LegacyApp />
        </Suspense>
      </LazyTreeBoundary>
    );
  }
  return (
    <LazyTreeBoundary fallback={<TreeLoadFailed />}>
      <Suspense fallback={<TreeLoading />}>
        <NewApp />
      </Suspense>
    </LazyTreeBoundary>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-unlisted-very-light-gray">
      <ReactKeycloakProvider authClient={keycloak} initOptions={keycloakInitOptions}>
        <TimezoneProvider>
          <BrowserRouter>
            <VersionedTree />
          </BrowserRouter>
        </TimezoneProvider>
      </ReactKeycloakProvider>
    </div>
  );
}

export default App;
